import React, { useEffect, useRef, useState } from 'react';
import PardotForm from 'components/PardotForm';
import {
    BlogImage,
    BlogText,
    Section,
    Sections,
    DetailWrap,
    BlogWistiaVideo,
    BlogLeftVideo,
    SectionVideo,
    SectionVideoDesc,
    TextWithImage
} from './elements'; // Assuming './elements' is the correct path

import AuthorInfo from '../AuthorInfo';
import RichText from 'components/RichText';
import WistiaPlayer from "../../../components/WistiaPlayer/WistiaPlayer";
import { Caption, TitleMd } from "../../../components/elements"; // Assuming correct path
import BlogSlider from "../BlogSlider";
import WistiaPopupEmbed from "../../../components/WistiaPopupEmbed";
import { Link } from "react-router-dom";
import PodcastBlock from "../../../components/PodcastBlock";
import DownloadSection from 'components/DownloadArea';
import TranscriptAndContentOutline from '../TranscriptAndContentOutline';
import Quotes from '../Quotes';

// Placeholder for uniqueKey if not provided, though map index should be used
const generateUniqueKey = () => Math.random().toString(36).substring(7);

const renderSection = (section, quoteWidth, uniqueKey) => {
    // If we get something other than a "region," create a region out of it
    // and store it in `section.elements`.
    if (section.ctype !== 'region') {
        if (
            section.ctype !== 'image' &&
            section.ctype !== 'textArea' &&
            section.ctype !== 'video' &&
            section.ctype !== 'imageSlider' &&
            section.ctype !== 'audio' &&
            section.ctype !== 'downloadArea' &&
            section.ctype !== 'transcriptAndContentOutline' &&
            section.ctype !== 'quotes' &&
            section.ctype !== 'pardotForm'
        ) {
            return <div key={uniqueKey}></div>;
        }

        section = {
            elements: [section],
            placement: section.placement
        };
    }

    if (!section.elements?.length) return <div key={uniqueKey}></div>;

    let size = '';
    let placement = '';

    switch (section.placement) {
        default:
        case 'Centered':
            size = window.innerWidth <= 1440 ? '800px' : '900px';
            placement = 'center';
            break;

        case 'Full-Width':
            size = '100%';
            placement = 'center';
            break;

        case 'Left-Justified':
            size = quoteWidth + 'px';
            placement = 'flex-start';
            break;

        case 'Right-Justified':
            size = quoteWidth + 'px';
            placement = 'flex-end';
            break;
    }

    const ank = section.elements[0]?.anchor
        ? section.elements[0].anchor.substr(1)
        : '';

    return (
        <Section
            id={ank}
            key={`section-${ank}-${uniqueKey}`}
            placement={placement}
        >
            {section.elements.map((e, k) => renderElement(e, `${uniqueKey}-${k}`))}
        </Section>
    );
};

const renderElement = (e, uniqueKey) => { // uniqueKey here is the map index 'k'
    // Render audio (podcast) blocks
    if (e.ctype === 'audio') {
        return <PodcastBlock {...e} key={`audio-${uniqueKey}`} />;
    }

    // Render Pardot forms
    if (e.ctype === 'pardotForm') {
        return (
            <PardotForm
                height={e.height}
                key={`pardot-${uniqueKey}`}
                pardotLink={e.pardotUrl}
            />
        );
    }

    // Render transcripts & content outlines
    if (e.ctype === 'transcriptAndContentOutline') {
        return (
            <TranscriptAndContentOutline
                key={`transcript-${uniqueKey}`}
                title={e.title}
                transcript={e.transcript}
                transcriptAndContent={e.transcriptAndContent}
            />
        );
    }

    // Render download area
    if (e.ctype === 'downloadArea') {
        return <DownloadSection key={`download-${uniqueKey}`} {...e} />;
    }

    // Render quotes
    if (e.ctype === 'quotes') {
        return (
            <Quotes
                key={`quotes-${uniqueKey}`}
                title={e.title}
                text={e.text}
                attribution={e.attribution}
            />
        );
    }

    // Render text areas (with optional images)
    if (e.ctype === 'textArea') {
        const hasImage = typeof e.image === 'string' ? e.image : (e.image && e.image.file && e.image.file.url);
        const imagePosition = (e.textWrap?.toLowerCase() === 'right' || e.textWrap?.toLowerCase() === 'left') ? 
            (e.textWrap?.toLowerCase() === 'right' ? 'left' : 'right') : 'right';
        const isMobile = window.innerWidth <= 768;

        if (hasImage) {
            const imageStyle = {
                maxWidth: isMobile ? '100%' : '50%',
                height: 'auto',
                float: isMobile ? 'none' : imagePosition,
                display: 'block',
                marginRight: isMobile ? '0' : (imagePosition === 'left' ? '20px' : '0'),
                marginLeft: isMobile ? '0' : (imagePosition === 'right' ? '20px' : '0'),
                marginBottom: isMobile ? '20px' : '10px'
            };

            return (
                <BlogText key={`textArea-img-${e.cfid || ''}-${uniqueKey}`}>
                    <div style={{ 
                        overflow: 'auto',
                        display: isMobile ? 'flex' : 'block',
                        flexDirection: isMobile ? 'column' : 'row'
                    }}>
                        <img
                            src={typeof e.image === 'string' ? e.image : e.image.file.url}
                            alt={e.imageAlt || 'Illustrative Image'}
                            style={imageStyle}
                            className="blog-content-image"
                        />
                        <div className="text-content" style={{
                            display: 'block',
                            float: isMobile ? 'none' : 'none',
                            clear: 'none',
                            overflow: 'visible',
                            overflowWrap: 'break-word',
                            width: isMobile ? '100%' : 'auto'
                        }}>
                            <RichText body={e.text} options={{ color: 'black' }} />
                        </div>
                    </div>
                </BlogText>
            );
        } else {
            // Case for no image
            return (
                <BlogText key={`textArea-noimg-${e.cfid || ''}-${uniqueKey}`}>
                    <div className="text-content">
                        <RichText body={e.text} options={{ color: 'black' }} />
                    </div>
                </BlogText>
            );
        }
    } 

    // Render image sliders
    if (e.ctype === 'imageSlider') {
        return <BlogSlider key={`slider-${uniqueKey}`} slides={e?.images} />;
    }

    // Render video
    if (e.ctype === 'video') {
        let arr = e?.videoUrl?.split('/');
        let isChannel = arr?.includes('channel');

        if (e.description && e.videoUrl) {
            return (
                <SectionVideo key={`videoDesc-${uniqueKey}`}>
                    <BlogLeftVideo>
                        <WistiaPopupEmbed playButton={true} customClass="wistia_custom" hashedId={arr[arr?.length - 1]} isChannel={isChannel} />
                        <Caption>{e.headline}</Caption>
                    </BlogLeftVideo>
                    <SectionVideoDesc>
                        <RichText body={e.description} />
                    </SectionVideoDesc>
                </SectionVideo>
            );
        } else if (e.videoUrl) {
            return (
                <BlogWistiaVideo key={`video-${uniqueKey}`}>
                    <TitleMd lineHeight="1.15">{e.headline}</TitleMd>
                    <div>
                        <WistiaPlayer controlsVisibleOnLoad={false} customClass="wistia_custom" hashedId={arr[arr.length - 1]} isChannel={isChannel} />
                    </div>
                </BlogWistiaVideo>
            );
        }
    }

    return <div key={`unknown-${e.ctype}-${uniqueKey}-${generateUniqueKey()}`}></div>;
};

function BlogDetail({ authors, body, sections }) {
    const sectionRef = useRef(null);
    const [quoteWidth, setQuoteWidth] = useState(1200); 
    const timeoutId = useRef(null); 

    const resizeObserver = () => {
        if (timeoutId.current) clearTimeout(timeoutId.current);
        timeoutId.current = setTimeout(() => {
            if (sectionRef.current) {
                const currentWindowWidth = Number(window.innerWidth);
                const clientWidth = Number(sectionRef.current.clientWidth);
                let windowWidthForCalc = currentWindowWidth >= 2500 - (currentWindowWidth * 0.3) ? 2420 : clientWidth;
                const baseWidth = currentWindowWidth <= 1440 ? 800 : 900;
                let sectionContentWidth = windowWidthForCalc - baseWidth;
                let dividedWidth = sectionContentWidth / 2;
                setQuoteWidth(dividedWidth + baseWidth);
            }
        }, 250); 
    };

    useEffect(() => {
        if (sectionRef.current) {
            const currentWindowWidth = Number(window.innerWidth);
            const clientWidth = Number(sectionRef.current.clientWidth);
            let windowWidthForCalc = currentWindowWidth >= 2500 - (currentWindowWidth * 0.3) ? 2420 : clientWidth;
            const baseWidth = currentWindowWidth <= 1440 ? 800 : 900;
            let sectionContentWidth = windowWidthForCalc - baseWidth;
            let dividedWidth = sectionContentWidth / 2;
            setQuoteWidth(dividedWidth + baseWidth);
        }
        window.addEventListener('resize', resizeObserver, true);
        return () => {
            window.removeEventListener('resize', resizeObserver, true);
            if (timeoutId.current) clearTimeout(timeoutId.current); 
        };
    }, []); 

    if (sections) {
        return (
            <Sections ref={sectionRef}>
                {sections.map((s, i) => renderSection(s, quoteWidth, i))}
                <Section className="author-section" placement="center"> 
                    <AuthorInfo authors={authors} />
                </Section>
            </Sections>
        );
    } else if (body) {
        return (
            <DetailWrap>
                <RichText body={body} options={{ color: 'black' }} />
                <Section placement="center"> 
                    <AuthorInfo authors={authors} />
                </Section>
            </DetailWrap>
        );
    }
    return <div></div>;
}

export default BlogDetail;
